import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  FileText,
  User2,
  ChevronRight,
  Image as ImageIcon,
  Plus,
  X,
  Search,
  Trash2,
  Edit2,
  Save,
} from "lucide-react";
import {
  TextField,
  InputAdornment,
  FormControl,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
} from "@mui/material";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { materials, designs } from "./dummyProductionData";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

// Dummy data for store materials
const storeProcessedMaterials = [
  { id: 1, name: "Processed Cotton Fabric", unit: "meters", inStock: 500 },
  { id: 2, name: "Dyed Silk", unit: "yards", inStock: 300 },
  { id: 3, name: "Treated Wool", unit: "meters", inStock: 250 },
  { id: 4, name: "Processed Leather", unit: "sq.ft", inStock: 1000 },
  { id: 5, name: "Finished Denim", unit: "yards", inStock: 800 },
];

const ReceiveJobslip = () => {
  const [receiveMode, setReceiveMode] = useState("overall");
  const [designReceiveMode, setDesignReceiveMode] = useState("overall");
  const navigate = useNavigate();
  const { id } = useParams();

  const [form, setForm] = useState({
    receivedDateTime: dayjs(),
    receivedBy: "Suresh", // Non-editable
    challanNo: "",
    notes: "",
  });
  const [selectedProcessedMaterial, setSelectedProcessedMaterial] =
    useState("");
  const [processedMaterialRows, setProcessedMaterialRows] = useState({});
  const [processedMaterialList, setProcessedMaterialList] = useState([]);
  const [itemNotes, setItemNotes] = useState({});
  const [itemImages, setItemImages] = useState({});
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [currentItemId, setCurrentItemId] = useState(null);
  const [currentNote, setCurrentNote] = useState("");

  const [errors, setErrors] = useState({});
  const [isMaterialSectionCollapsed, setIsMaterialSectionCollapsed] =
    useState(true);
  const [isDesignSectionCollapsed, setIsDesignSectionCollapsed] =
    useState(true);
  const [receiveQuantities, setReceiveQuantities] = useState({});
  const [receiveAll, setReceiveAll] = useState(false);
  const [addToStock, setAddToStock] = useState(false);
  const [receiveDesignQuantities, setReceiveDesignQuantities] = useState({});
  const [receiveAllDesigns, setReceiveAllDesigns] = useState(false);
  const [readyForPackaging, setReadyForPackaging] = useState(false);
  const [addToStockDesigns, setAddToStockDesigns] = useState(false);
  const [paymentDue, setPaymentDue] = useState(false);
  const [
    isProcessedMaterialSectionCollapsed,
    setIsProcessedMaterialSectionCollapsed,
  ] = useState(true);
  const [showAddMaterialModal, setShowAddMaterialModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Change tempSelectedMaterial to tempSelectedMaterials array
  const [tempSelectedMaterials, setTempSelectedMaterials] = useState([]);
  // Add new state for processed status
  const [processedStatus, setProcessedStatus] = useState({});
  // Add new state for processed material details
  const [processedMaterialDetails, setProcessedMaterialDetails] = useState({});

  // Add new state for taka functionality
  const [showTakaModal, setShowTakaModal] = useState(false);
  const [selectedColorRow, setSelectedColorRow] = useState(null);
  const [takaInputFields, setTakaInputFields] = useState({});
  const [newTakaQuantity, setNewTakaQuantity] = useState("");
  const [newTakaValue, setNewTakaValue] = useState("");

  // New state for other designs
  const [showOtherDesignsModal, setShowOtherDesignsModal] = useState(false);
  const [otherDesignSearchQuery, setOtherDesignSearchQuery] = useState("");
  const [tempSelectedOtherDesigns, setTempSelectedOtherDesigns] = useState([]);
  const [otherDesigns, setOtherDesigns] = useState([]);
  const [showSizeDenominationModal, setShowSizeDenominationModal] =
    useState(false);
  const [currentOtherDesignColor, setCurrentOtherDesignColor] = useState(null);

  // Dummy data for colors
  const colors = [
    "Red",
    "Blue",
    "Green",
    "Yellow",
    "Black",
    "White",
    "Grey",
    "Navy",
    "Purple",
    "Orange",
  ];

  // Add new state for receive material taka
  const [receiveMaterialTakaFields, setReceiveMaterialTakaFields] = useState(
    {},
  );
  const [selectedReceiveMaterialRow, setSelectedReceiveMaterialRow] =
    useState(null);

  // Using only first material and design for demo
  const receiveMaterials = materials.slice(0, 2);
  const receiveDesigns = designs.slice(0, 1); // Take only first design

  // Get remaining designs for the modal
  const availableOtherDesigns = designs.slice(1);

  const handleChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBack = () => {
    navigate(`/production/jobslips/${id}`);
  };

  const handleReceiveQuantityChange = (materialId, color, value) => {
    setReceiveQuantities(prev => ({
      ...prev,
      [materialId]: {
        ...prev[materialId],
        [color]: value,
      },
    }));
  };

  const handleReceiveAllChange = event => {
    setReceiveAll(event.target.checked);
    if (event.target.checked) {
      const allQuantities = {};
      receiveMaterials.forEach(material => {
        allQuantities[material.id] = {};
        material.colors.split(", ").forEach(color => {
          allQuantities[material.id][color] = material.inStock;
        });
      });
      setReceiveQuantities(allQuantities);
    } else {
      const resetQuantities = {};
      receiveMaterials.forEach(material => {
        resetQuantities[material.id] = {};
        material.colors.split(", ").forEach(color => {
          resetQuantities[material.id][color] = 0;
        });
      });
      setReceiveQuantities(resetQuantities);
    }
  };

  const handleReceiveDesignQuantityChange = (
    designId,
    pieceType,
    color,
    size,
    value,
  ) => {
    setReceiveDesignQuantities(prev => ({
      ...prev,
      [designId]: {
        ...prev[designId],
        [pieceType]: {
          ...prev[designId]?.[pieceType],
          [color]: {
            ...prev[designId]?.[pieceType]?.[color],
            [size]: value,
          },
        },
      },
    }));
  };

  const handleDesignActionChange = action => {
    if (action === "packaging") {
      setReadyForPackaging(prev => {
        if (prev) {
          // If currently checked, uncheck it
          return false;
        } else {
          // If unchecked, check it and uncheck the other
          setAddToStockDesigns(false);
          return true;
        }
      });
    } else if (action === "stock") {
      setAddToStockDesigns(prev => {
        if (prev) {
          // If currently checked, uncheck it
          return false;
        } else {
          // If unchecked, check it and uncheck the other
          setReadyForPackaging(false);
          return true;
        }
      });
    }
  };

  const handleProcessedMaterialDone = () => {
    const selectedMaterial = materials.find(
      m => m.id === selectedProcessedMaterial,
    );
    if (
      selectedMaterial &&
      !processedMaterialList.find(m => m.id === selectedMaterial.id)
    ) {
      setProcessedMaterialList(prev => [...prev, selectedMaterial]);
      setProcessedMaterialRows(prev => ({
        ...prev,
        [selectedMaterial.id]: [
          { color: "", quantity: 1, unit: selectedMaterial.unit },
        ],
      }));
    }
    setSelectedProcessedMaterial("");
  };

  const handleAddMaterialColorRow = materialId => {
    setProcessedMaterialRows(prev => ({
      ...prev,
      [materialId]: [
        ...(prev[materialId] || []),
        {
          color: "",
          quantity: 1,
          unit: materials.find(m => m.id === materialId)?.unit || "meters",
        },
      ],
    }));
  };

  const handleRowChange = (materialId, index, field, value) => {
    setProcessedMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].map((row, i) =>
        i === index ? { ...row, [field]: value } : row,
      ),
    }));
  };

  const handleRemoveRow = (materialId, index) => {
    setProcessedMaterialRows(prev => ({
      ...prev,
      [materialId]: prev[materialId].filter((_, i) => i !== index),
    }));
  };

  const handleRemoveItem = itemId => {
    setProcessedMaterialList(prev => prev.filter(item => item.id !== itemId));
    const newMaterialRows = { ...processedMaterialRows };
    delete newMaterialRows[itemId];
    setProcessedMaterialRows(newMaterialRows);
  };

  const handleAddNote = itemId => {
    setCurrentItemId(itemId);
    setCurrentNote(itemNotes[itemId] || "");
    setShowNoteModal(true);
  };

  const handleSaveNote = () => {
    if (currentItemId) {
      setItemNotes(prev => ({
        ...prev,
        [currentItemId]: currentNote,
      }));
      setShowNoteModal(false);
    }
  };

  const handleImageUpload = (itemId, event) => {
    const files = Array.from(event.target.files);
    const newImages = files.map(file => URL.createObjectURL(file));
    setItemImages(prev => ({
      ...prev,
      [itemId]: [...(prev[itemId] || []), ...newImages],
    }));
  };

  const handleRemoveImage = (itemId, imageIndex) => {
    setItemImages(prev => ({
      ...prev,
      [itemId]: prev[itemId].filter((_, index) => index !== imageIndex),
    }));
  };

  const handleAddMaterial = () => {
    setShowAddMaterialModal(true);
    setSearchQuery("");
    setTempSelectedMaterials([]);
  };

  const handleMaterialSelect = material => {
    setTempSelectedMaterials(prev => {
      const exists = prev.find(m => m.id === material.id);
      if (exists) {
        return prev.filter(m => m.id !== material.id);
      } else {
        return [...prev, material];
      }
    });
  };

  const handleAddSelectedMaterials = () => {
    const newMaterials = tempSelectedMaterials.filter(
      material => !processedMaterialList.find(m => m.id === material.id),
    );

    if (newMaterials.length > 0) {
      setProcessedMaterialList(prev => [...prev, ...newMaterials]);
      const newRows = {};
      newMaterials.forEach(material => {
        newRows[material.id] = [
          { color: "", quantity: 1, unit: material.unit },
        ];
      });
      setProcessedMaterialRows(prev => ({
        ...prev,
        ...newRows,
      }));
    }
    setShowAddMaterialModal(false);
    setTempSelectedMaterials([]);
  };

  const filteredMaterials = materials.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.colors.toLowerCase().includes(searchLower)
    );
  });

  // Add handler for processed status change
  const handleProcessedStatusChange = (materialId, status) => {
    setProcessedStatus(prev => ({
      ...prev,
      [materialId]: status,
    }));
  };

  // Add handler for processed material details change
  const handleProcessedDetailsChange = (materialId, field, value) => {
    setProcessedMaterialDetails(prev => ({
      ...prev,
      [materialId]: {
        ...(prev[materialId] || {}),
        [field]: value,
      },
    }));
  };

  // Add taka handlers
  const handleAddTakaClick = (materialId, rowIndex) => {
    setSelectedColorRow({ materialId, rowIndex });
    setShowTakaModal(true);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  // Modify handleAddTakaDenomination to handle both sections
  const handleAddTakaDenomination = () => {
    if (!newTakaQuantity || !newTakaValue) return;

    if (selectedColorRow) {
      // Handle processed material taka
      const { materialId, rowIndex } = selectedColorRow;
      const rowKey = `${materialId}-${rowIndex}`;

      setTakaInputFields(prev => ({
        ...prev,
        [rowKey]: [
          ...(prev[rowKey] || []),
          {
            quantity: newTakaQuantity,
            value: newTakaValue,
            id: Date.now(),
          },
        ],
      }));
    } else if (selectedReceiveMaterialRow) {
      // Handle receive material taka
      const { materialId, color } = selectedReceiveMaterialRow;
      const rowKey = `${materialId}-${color}`;

      setReceiveMaterialTakaFields(prev => ({
        ...prev,
        [rowKey]: [
          ...(prev[rowKey] || []),
          {
            quantity: newTakaQuantity,
            value: newTakaValue,
            id: Date.now(),
          },
        ],
      }));
    }

    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const handleRemoveTakaField = (rowKey, fieldId) => {
    setTakaInputFields(prev => ({
      ...prev,
      [rowKey]: prev[rowKey]?.filter(field => field.id !== fieldId) || [],
    }));
  };

  // Modify handleCloseTakaModal to handle both sections
  const handleCloseTakaModal = () => {
    setShowTakaModal(false);
    setSelectedColorRow(null);
    setSelectedReceiveMaterialRow(null);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  const shouldShowTakaButton = unit => {
    return ["meters", "kg", "yards"].includes(unit);
  };

  // Add handler for receive material taka
  const handleReceiveMaterialTakaClick = (materialId, color) => {
    setSelectedReceiveMaterialRow({ materialId, color });
    setShowTakaModal(true);
    setNewTakaQuantity("");
    setNewTakaValue("");
  };

  // Add handler for removing receive material taka
  const handleRemoveReceiveMaterialTaka = (rowKey, fieldId) => {
    setReceiveMaterialTakaFields(prev => ({
      ...prev,
      [rowKey]: prev[rowKey]?.filter(field => field.id !== fieldId) || [],
    }));
  };

  // Handlers for other designs
  const handleOtherDesignSelect = design => {
    setTempSelectedOtherDesigns(prev => {
      const exists = prev.find(d => d.id === design.id);
      if (exists) {
        return prev.filter(d => d.id !== design.id);
      } else {
        return [
          ...prev,
          {
            ...design,
            pieces: [
              {
                pieceType: "Full",
                colors: [],
                jobRate: "",
                sizeRatio: {},
              },
            ],
          },
        ];
      }
    });
  };

  const handleAddSelectedOtherDesigns = () => {
    const newDesigns = tempSelectedOtherDesigns.filter(
      design => !otherDesigns.find(d => d.id === design.id),
    );

    if (newDesigns.length > 0) {
      setOtherDesigns(prev => [...prev, ...newDesigns]);
    }
    setShowOtherDesignsModal(false);
    setTempSelectedOtherDesigns([]);
  };

  const handleAddOtherDesignColorRow = (designId, pieceIndex) => {
    setOtherDesigns(prev =>
      prev.map(design => {
        if (design.id === designId) {
          const updatedPieces = [...design.pieces];
          updatedPieces[pieceIndex] = {
            ...updatedPieces[pieceIndex],
            colors: [
              ...(updatedPieces[pieceIndex].colors || []),
              { name: "", quantity: "", unit: "pieces", sizes: {} },
            ],
          };
          return { ...design, pieces: updatedPieces };
        }
        return design;
      }),
    );
  };

  const handleOtherDesignColorChange = (
    designId,
    pieceIndex,
    colorIndex,
    field,
    value,
  ) => {
    setOtherDesigns(prev =>
      prev.map(design => {
        if (design.id === designId) {
          const updatedPieces = [...design.pieces];
          const updatedColors = [...updatedPieces[pieceIndex].colors];
          updatedColors[colorIndex] = {
            ...updatedColors[colorIndex],
            [field]: value,
          };
          updatedPieces[pieceIndex] = {
            ...updatedPieces[pieceIndex],
            colors: updatedColors,
          };
          return { ...design, pieces: updatedPieces };
        }
        return design;
      }),
    );
  };

  const handleRemoveOtherDesign = designId => {
    setOtherDesigns(prev => prev.filter(design => design.id !== designId));
  };

  const handleRemoveOtherDesignColor = (designId, pieceIndex, colorIndex) => {
    setOtherDesigns(prev =>
      prev.map(design => {
        if (design.id === designId) {
          const updatedPieces = [...design.pieces];
          updatedPieces[pieceIndex] = {
            ...updatedPieces[pieceIndex],
            colors: updatedPieces[pieceIndex].colors.filter(
              (_, idx) => idx !== colorIndex,
            ),
          };
          return { ...design, pieces: updatedPieces };
        }
        return design;
      }),
    );
  };

  const handleAddSizeDenomination = (designId, colorIndex) => {
    setCurrentOtherDesignColor({ designId, colorIndex });
    setShowSizeDenominationModal(true);
  };

  const handleSizeDenominationChange = (size, value) => {
    if (!currentOtherDesignColor) return;

    setOtherDesigns(prev =>
      prev.map(design => {
        if (design.id === currentOtherDesignColor.designId) {
          const updatedPieces = [...design.pieces];
          const updatedColors = [...updatedPieces[0].colors];
          updatedColors[currentOtherDesignColor.colorIndex] = {
            ...updatedColors[currentOtherDesignColor.colorIndex],
            sizes: {
              ...updatedColors[currentOtherDesignColor.colorIndex].sizes,
              [size]: value,
            },
          };
          updatedPieces[0] = {
            ...updatedPieces[0],
            colors: updatedColors,
          };
          return { ...design, pieces: updatedPieces };
        }
        return design;
      }),
    );
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBack}
            className="p-1.5 rounded-md bg-gray-50 hover:bg-gray-100 transition-colors">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Receive Job Slip
          </h1>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {/* Basic Information */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-base font-medium text-gray-800 mb-4">
              Basic Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Date and Time of Received */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Date and Time of Received"
                  value={form.receivedDateTime}
                  onChange={newValue =>
                    handleChange("receivedDateTime", newValue)
                  }
                  slotProps={{
                    textField: {
                      size: "small",
                      required: true,
                      error: Boolean(errors.receivedDateTime),
                      helperText: errors.receivedDateTime,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Clock size={18} className="text-[#5652B0]" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              {/* Received By - Non-editable */}
              <TextField
                label="Received By"
                size="small"
                value={form.receivedBy}
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <User2 size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Challan No */}
              <TextField
                label="Receive Challan No."
                size="small"
                required
                value={form.challanNo}
                onChange={e => handleChange("challanNo", e.target.value)}
                error={Boolean(errors.challanNo)}
                helperText={errors.challanNo}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Notes - Full width in new row */}
              <TextField
                label="Notes"
                multiline
                rows={1}
                fullWidth
                size="small"
                value={form.notes}
                onChange={e => handleChange("notes", e.target.value)}
                placeholder="Add any additional notes here..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FileText size={18} className="text-[#5652B0]" />
                    </InputAdornment>
                  ),
                }}
                className="md:col-span-3"
              />
            </div>
          </div>

          {/* Receive Material Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsMaterialSectionCollapsed(prev => !prev)}>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isMaterialSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <div className="flex items-center gap-4">
                  <h2 className="text-base font-medium text-gray-800">
                    Receive Materials
                  </h2>
                  {!isMaterialSectionCollapsed && (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 180,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                          },
                          "& fieldset": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover fieldset": {
                            borderColor: "#94a3b8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                      }}>
                      <Select
                        value={receiveMode}
                        onChange={e => setReceiveMode(e.target.value)}
                        displayEmpty
                        className="text-sm font-medium"
                        onClick={e => e.stopPropagation()}
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                          },
                        }}>
                        <MenuItem
                          value="overall"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Overall Receive
                        </MenuItem>
                        <MenuItem
                          value="colour_wise"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Colour Wise Receive
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </div>
                {isMaterialSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {receiveMaterials.length > 0 ? (
                      <>
                        :{" "}
                        {receiveMaterials.map(
                          (item, index) =>
                            item.name +
                            (index < receiveMaterials.length - 1 ? ", " : ""),
                        )}
                      </>
                    ) : (
                      ": No materials added yet"
                    )}
                  </span>
                )}
              </div>
              {!isMaterialSectionCollapsed && (
                <div className="flex items-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={receiveAll}
                        onChange={handleReceiveAllChange}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Receive All
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={addToStock}
                        onChange={e => setAddToStock(e.target.checked)}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Add to Stock
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                </div>
              )}
            </div>

            <div
              className={`space-y-4 ${
                isMaterialSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              {receiveMaterials.map(material => (
                <div
                  key={material.id}
                  className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-start gap-4">
                      <img
                        src={material.image}
                        alt={material.name}
                        className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start flex-col">
                            <div className="flex items-center gap-4">
                              <h3 className="text-lg font-medium text-gray-800 ml-0">
                                {material.name}
                              </h3>
                              <span className="text-sm text-gray-600">
                                {material.usedQty || 0}/
                                {material.totalQty || material.inStock}{" "}
                                {material.unit}
                              </span>
                              <div className="flex items-center gap-2">
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Receive Quantity"
                                  className="min-w-[150px]"
                                  disabled={receiveMode === "colour_wise"}
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {material.unit}
                                      </InputAdornment>
                                    ),
                                  }}
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "8px",
                                      backgroundColor:
                                        receiveMode === "colour_wise"
                                          ? "#f3f4f6"
                                          : "transparent",
                                      "& fieldset": {
                                        borderColor:
                                          receiveMode === "colour_wise"
                                            ? "#d1d5db"
                                            : "#e5e7eb",
                                      },
                                      "&:hover fieldset": {
                                        borderColor:
                                          receiveMode === "colour_wise"
                                            ? "#d1d5db"
                                            : "#9ca3af",
                                      },
                                      "& input": {
                                        color:
                                          receiveMode === "colour_wise"
                                            ? "#9ca3af"
                                            : "inherit",
                                      },
                                    },
                                    "& .MuiInputLabel-root": {
                                      color:
                                        receiveMode === "colour_wise"
                                          ? "#9ca3af"
                                          : "#6b7280",
                                      fontWeight: 300,
                                    },
                                  }}
                                />
                                {shouldShowTakaButton(material.unit) && (
                                  <button
                                    type="button"
                                    disabled={receiveMode === "colour_wise"}
                                    onClick={() =>
                                      handleReceiveMaterialTakaClick(
                                        material.id,
                                        "default",
                                      )
                                    }
                                    className={`px-3 py-1.5 text-xs font-medium border rounded-lg transition-all ${
                                      receiveMode === "colour_wise"
                                        ? "text-gray-400 border-gray-300 bg-gray-50 cursor-not-allowed"
                                        : "text-[#5652B0] border-[#5652B0] hover:bg-[#5652B0] hover:text-white"
                                    }`}>
                                    {receiveMaterialTakaFields[
                                      `${material.id}-default`
                                    ]?.length > 0
                                      ? "Edit Taka"
                                      : "Add Taka"}
                                  </button>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {material.unit}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="space-y-4">
                      {receiveMode === "colour_wise" &&
                        material.colors.split(", ").map(color => (
                          <div key={color} className="flex flex-col">
                            <div className="flex items-center gap-4">
                              <div className="min-w-[120px]">
                                <span className="text-sm font-medium text-gray-700">
                                  {color}
                                </span>
                              </div>
                              <div className="min-w-[200px]">
                                <span className="text-sm text-gray-600">
                                  {receiveQuantities[material.id]?.[color] || 0}
                                  /{material.inStock} {material.unit}
                                </span>
                              </div>
                              <div className="min-w-[150px]">
                                <TextField
                                  fullWidth
                                  size="small"
                                  type="number"
                                  label="Receive Quantity"
                                  value={
                                    receiveQuantities[material.id]?.[color] ||
                                    ""
                                  }
                                  onChange={e =>
                                    handleReceiveQuantityChange(
                                      material.id,
                                      color,
                                      e.target.value,
                                    )
                                  }
                                  InputProps={{
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        {material.unit}
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </div>

                              {shouldShowTakaButton(material.unit) && (
                                <div className="min-w-[100px]">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleReceiveMaterialTakaClick(
                                        material.id,
                                        color,
                                      )
                                    }
                                    className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                    {receiveMaterialTakaFields[
                                      `${material.id}-${color}`
                                    ]?.length > 0
                                      ? "Edit Taka"
                                      : "Add Taka"}
                                  </button>
                                </div>
                              )}
                            </div>

                            {receiveMaterialTakaFields[
                              `${material.id}-${color}`
                            ]?.length > 0 && (
                              <div className="ml-4 flex items-center gap-2 mt-2">
                                <span className="text-xs text-gray-600 font-medium">
                                  Taka:
                                </span>
                                <div className="flex flex-wrap gap-1">
                                  {receiveMaterialTakaFields[
                                    `${material.id}-${color}`
                                  ].map(field => (
                                    <span
                                      key={field.id}
                                      className="px-2 py-1 bg-gray-100 text-xs font-medium rounded">
                                      {field.quantity}x{field.value}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Receive Processed Material Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() =>
                setIsProcessedMaterialSectionCollapsed(prev => !prev)
              }>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isProcessedMaterialSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <h2 className="text-base font-medium text-gray-800">
                  Receive Processed Materials
                </h2>
                {isProcessedMaterialSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {processedMaterialList.length > 0 ? (
                      <>
                        :{" "}
                        {processedMaterialList.map(
                          (item, index) =>
                            item.name +
                            (index < processedMaterialList.length - 1
                              ? ", "
                              : ""),
                        )}
                      </>
                    ) : (
                      ": No processed materials added yet"
                    )}
                  </span>
                )}
              </div>
            </div>

            <div
              className={`space-y-4 ${
                isProcessedMaterialSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              <div className="flex items-center gap-4">
                <button
                  onClick={handleAddMaterial}
                  className="px-4 py-2 text-[#5652B0] font-medium border-2 border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-colors w-[40%] flex items-center justify-center gap-2">
                  <Plus size={16} />
                  Select Material
                </button>

                <span className="text-sm font-medium text-gray-500">OR</span>

                <button
                  className="px-4 py-2 text-[#5652B0] font-medium border-2 border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-colors"
                  onClick={() => {
                    /* Add handler later */
                  }}>
                  Add New Material
                </button>
              </div>

              {/* Display Selected Materials */}
              {processedMaterialList.length > 0 && (
                <div className="space-y-4 mt-4">
                  {processedMaterialList.map(item => (
                    <div
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-start gap-4">
                          <img
                            src={
                              item.image ||
                              "https://images.unsplash.com/photo-1518893063132-36e46dbe2428"
                            }
                            alt={item.name}
                            className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start flex-col">
                                <h3 className="text-lg font-medium text-gray-800">
                                  {item.name}
                                </h3>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {item.colors} • {item.unit}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleAddNote(item.id)}
                                  className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors">
                                  <FileText size={18} />
                                </button>
                                <label className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors cursor-pointer">
                                  <ImageIcon size={18} />
                                  <input
                                    type="file"
                                    className="hidden"
                                    multiple
                                    accept="image/*"
                                    onChange={e =>
                                      handleImageUpload(item.id, e)
                                    }
                                  />
                                </label>
                                <button
                                  onClick={() => handleRemoveItem(item.id)}
                                  className="p-2 text-[#9B1C1C] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Note Display Section */}
                      {itemNotes[item.id] && (
                        <div className="px-4 py-3 border-t border-gray-100">
                          <div className="flex items-start gap-2">
                            <FileText
                              size={16}
                              className="text-gray-500 mt-0.5"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Note:
                              </div>
                              <div className="text-sm text-gray-600">
                                {itemNotes[item.id]}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Image Preview Section */}
                      {itemImages[item.id] &&
                        itemImages[item.id].length > 0 && (
                          <div className="px-4 py-3 border-t border-gray-100">
                            <div className="flex flex-wrap gap-3">
                              {itemImages[item.id].map((image, index) => (
                                <div key={index} className="relative group">
                                  <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden">
                                    <img
                                      src={image}
                                      alt={`Preview ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRemoveImage(item.id, index)
                                    }
                                    className="absolute -top-1.5 -right-1.5 bg-white shadow-sm border border-gray-200 text-[#9B1C1C] rounded-full p-1 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100">
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      <div className="p-4">
                        <div className="flex flex-col gap-2">
                          {(processedMaterialRows[item.id] || []).map(
                            (row, index) => (
                              <div
                                key={index}
                                className="flex items-center gap-4">
                                <div className="min-w-[200px]">
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Color</InputLabel>
                                    <Select
                                      value={row.color}
                                      label="Color"
                                      onChange={e =>
                                        handleRowChange(
                                          item.id,
                                          index,
                                          "color",
                                          e.target.value,
                                        )
                                      }>
                                      {item.colors.split(", ").map(color => (
                                        <MenuItem key={color} value={color}>
                                          {color}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </div>

                                <div className="min-w-[120px]">
                                  <TextField
                                    fullWidth
                                    type="number"
                                    size="small"
                                    label="Quantity"
                                    value={row.quantity}
                                    onChange={e =>
                                      handleRowChange(
                                        item.id,
                                        index,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>

                                <div className="min-w-[120px]">
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Unit</InputLabel>
                                    <Select
                                      value={row.unit}
                                      label="Unit"
                                      onChange={e =>
                                        handleRowChange(
                                          item.id,
                                          index,
                                          "unit",
                                          e.target.value,
                                        )
                                      }>
                                      <MenuItem value="meters">Meters</MenuItem>
                                      <MenuItem value="yards">Yards</MenuItem>
                                      <MenuItem value="pieces">Pieces</MenuItem>
                                    </Select>
                                  </FormControl>
                                </div>

                                {shouldShowTakaButton(row.unit) && (
                                  <div className="min-w-[100px]">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleAddTakaClick(item.id, index)
                                      }
                                      className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                      {takaInputFields[`${item.id}-${index}`]
                                        ?.length > 0
                                        ? "Edit Taka"
                                        : "Add Taka"}
                                    </button>
                                  </div>
                                )}

                                <IconButton
                                  onClick={() =>
                                    handleRemoveRow(item.id, index)
                                  }
                                  size="small">
                                  <Trash2
                                    size={18}
                                    className="text-[#9B1C1C] hover:text-red-600"
                                  />
                                </IconButton>

                                {index ===
                                  (processedMaterialRows[item.id] || [])
                                    .length -
                                    1 && (
                                  <button
                                    onClick={() =>
                                      handleAddMaterialColorRow(item.id)
                                    }
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all ml-2">
                                    <Plus size={16} />
                                    Add Color
                                  </button>
                                )}
                              </div>
                            ),
                          )}

                          {/* Remove the standalone Add Color button */}
                          {(processedMaterialRows[item.id] || []).length ===
                            0 && (
                            <div className="flex">
                              <button
                                onClick={() =>
                                  handleAddMaterialColorRow(item.id)
                                }
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                                <Plus size={16} />
                                Add Color
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Add the processed material checkbox */}
                      <div className="ml-4 mt-0 flex">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={processedStatus[item.id] || false}
                              onChange={e =>
                                handleProcessedStatusChange(
                                  item.id,
                                  e.target.checked,
                                )
                              }
                              sx={{
                                color: "#5652B0",
                                "&.Mui-checked": {
                                  color: "#5652B0",
                                },
                              }}
                            />
                          }
                          label={
                            <span className="text-sm font-medium text-gray-700">
                              Is this processed material
                            </span>
                          }
                        />
                      </div>

                      {/* Add the processed material checkbox and fields */}
                      <div className="ml-4 mt-2 mb-4">
                        {processedStatus[item.id] && (
                          <div className="flex items-center gap-4 mt-3">
                            <div className="min-w-[200px]">
                              <FormControl size="small" fullWidth>
                                <InputLabel>Material Used</InputLabel>
                                <Select
                                  value={
                                    processedMaterialDetails[item.id]
                                      ?.materialUsed || ""
                                  }
                                  label="Material Used"
                                  onChange={e =>
                                    handleProcessedDetailsChange(
                                      item.id,
                                      "materialUsed",
                                      e.target.value,
                                    )
                                  }>
                                  {materials.map(material => (
                                    <MenuItem
                                      key={material.id}
                                      value={material.id}>
                                      {material.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </div>

                            <div className="min-w-[120px]">
                              <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Quantity"
                                value={
                                  processedMaterialDetails[item.id]?.quantity ||
                                  ""
                                }
                                onChange={e =>
                                  handleProcessedDetailsChange(
                                    item.id,
                                    "quantity",
                                    e.target.value,
                                  )
                                }
                              />
                            </div>

                            <div className="min-w-[120px]">
                              <FormControl size="small" fullWidth>
                                <InputLabel>Unit</InputLabel>
                                <Select
                                  value={
                                    processedMaterialDetails[item.id]?.unit ||
                                    ""
                                  }
                                  label="Unit"
                                  onChange={e =>
                                    handleProcessedDetailsChange(
                                      item.id,
                                      "unit",
                                      e.target.value,
                                    )
                                  }>
                                  <MenuItem value="meters">Meters</MenuItem>
                                  <MenuItem value="yards">Yards</MenuItem>
                                  <MenuItem value="pieces">Pieces</MenuItem>
                                  <MenuItem value="kg">Kilograms</MenuItem>
                                </Select>
                              </FormControl>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Add Note Modal */}
          <Dialog
            open={showNoteModal}
            onClose={() => setShowNoteModal(false)}
            maxWidth="sm"
            fullWidth>
            <DialogTitle>Add Note</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                label="Note"
                fullWidth
                multiline
                rows={4}
                value={currentNote}
                onChange={e => setCurrentNote(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowNoteModal(false)}>Cancel</Button>
              <Button
                onClick={handleSaveNote}
                variant="contained"
                sx={{
                  backgroundColor: "#5652B0",
                  "&:hover": { backgroundColor: "#4A47A3" },
                }}>
                Save Note
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Material Selection Modal */}
          <Dialog
            open={showAddMaterialModal}
            onClose={() => setShowAddMaterialModal(false)}
            maxWidth="md"
            fullWidth>
            <DialogTitle>
              <div className="flex items-center justify-between">
                <span>Select Materials</span>
                <IconButton
                  onClick={() => setShowAddMaterialModal(false)}
                  size="small">
                  <X size={18} />
                </IconButton>
              </div>
            </DialogTitle>
            <DialogContent>
              <div className="py-4">
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search materials..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={18} className="text-gray-500" />
                      </InputAdornment>
                    ),
                  }}
                />

                <div className="mt-4 space-y-2">
                  {filteredMaterials.map(item => (
                    <div
                      key={item.id}
                      onClick={() => handleMaterialSelect(item)}
                      className={`flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-all ${
                        tempSelectedMaterials.find(m => m.id === item.id)
                          ? "border-[#5652B0] bg-[#5652B0]/5"
                          : "border-gray-200 hover:border-[#5652B0] hover:bg-[#5652B0]/5"
                      }`}>
                      <Checkbox
                        checked={Boolean(
                          tempSelectedMaterials.find(m => m.id === item.id),
                        )}
                        onChange={() => handleMaterialSelect(item)}
                      />
                      <img
                        src={
                          item.image ||
                          "https://images.unsplash.com/photo-1518893063132-36e46dbe2428"
                        }
                        alt={item.name}
                        className="w-12 h-12 object-cover rounded-lg bg-gray-100"
                      />
                      <div className="flex-1">
                        <h3 className="text-base font-medium text-gray-800">
                          {item.name}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {item.colors} • {item.unit}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-800">
                          {item.inStock} {item.unit}
                        </div>
                        <div className="text-xs text-gray-500">in stock</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowAddMaterialModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddSelectedMaterials}
                variant="contained"
                disabled={tempSelectedMaterials.length === 0}
                sx={{
                  backgroundColor: "#5652B0",
                  "&:hover": { backgroundColor: "#4A47A3" },
                }}>
                Add Selected ({tempSelectedMaterials.length})
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Taka Modal */}
          <Dialog
            open={showTakaModal}
            onClose={handleCloseTakaModal}
            maxWidth="md"
            fullWidth>
            <DialogTitle>
              <div className="flex items-center justify-between">
                <span>Add Taka</span>
                <IconButton onClick={handleCloseTakaModal} size="small">
                  <X size={18} />
                </IconButton>
              </div>
            </DialogTitle>
            <DialogContent>
              <div className="py-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-4">
                    Taka Information
                  </h4>

                  <div className="flex flex-wrap gap-3 mb-6">
                    {selectedColorRow &&
                      takaInputFields[
                        `${selectedColorRow.materialId}-${selectedColorRow.rowIndex}`
                      ]?.map(field => (
                        <div
                          key={field.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {field.quantity}
                            </span>
                            <span className="text-xs text-gray-500">
                              {
                                processedMaterialRows[
                                  selectedColorRow.materialId
                                ][selectedColorRow.rowIndex].unit
                              }
                            </span>
                            <span className="text-gray-400">=</span>
                            <span className="text-sm font-medium text-gray-900">
                              {field.value}
                            </span>
                            <span className="text-xs text-gray-500">
                              pieces
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleRemoveTakaField(
                                `${selectedColorRow.materialId}-${selectedColorRow.rowIndex}`,
                                field.id,
                              )
                            }
                            className="text-red-500 hover:text-red-700 ml-2">
                            <X size={14} />
                          </button>
                        </div>
                      ))}

                    {selectedReceiveMaterialRow &&
                      receiveMaterialTakaFields[
                        `${selectedReceiveMaterialRow.materialId}-${selectedReceiveMaterialRow.color}`
                      ]?.map(field => (
                        <div
                          key={field.id}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {field.quantity}
                            </span>
                            <span className="text-xs text-gray-500">
                              {
                                materials.find(
                                  m =>
                                    m.id ===
                                    selectedReceiveMaterialRow.materialId,
                                )?.unit
                              }
                            </span>
                            <span className="text-gray-400">=</span>
                            <span className="text-sm font-medium text-gray-900">
                              {field.value}
                            </span>
                            <span className="text-xs text-gray-500">
                              pieces
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleRemoveReceiveMaterialTaka(
                                `${selectedReceiveMaterialRow.materialId}-${selectedReceiveMaterialRow.color}`,
                                field.id,
                              )
                            }
                            className="text-red-500 hover:text-red-700 ml-2">
                            <X size={14} />
                          </button>
                        </div>
                      ))}

                    {(!selectedColorRow ||
                      !takaInputFields[
                        `${selectedColorRow.materialId}-${selectedColorRow.rowIndex}`
                      ]?.length) &&
                      (!selectedReceiveMaterialRow ||
                        !receiveMaterialTakaFields[
                          `${selectedReceiveMaterialRow.materialId}-${selectedReceiveMaterialRow.color}`
                        ]?.length) && (
                        <div className="text-center py-8 text-gray-500 w-full">
                          <p>No taka entries found</p>
                          <p className="text-sm">
                            Taka entries will appear here when added
                          </p>
                        </div>
                      )}
                  </div>

                  {/* Add New Taka */}
                  <div className="flex gap-4 items-start mt-6">
                    <div className="min-w-[200px]">
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={newTakaQuantity}
                        onChange={e => setNewTakaQuantity(e.target.value)}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedColorRow
                                ? processedMaterialRows[
                                    selectedColorRow.materialId
                                  ][selectedColorRow.rowIndex].unit
                                : materials.find(
                                    m =>
                                      m.id ===
                                      selectedReceiveMaterialRow?.materialId,
                                  )?.unit}
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <div className="min-w-[200px]">
                      <TextField
                        fullWidth
                        label="Number of Pieces"
                        type="number"
                        value={newTakaValue}
                        onChange={e => setNewTakaValue(e.target.value)}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              pieces
                            </InputAdornment>
                          ),
                        }}
                      />
                    </div>
                    <button
                      onClick={handleAddTakaDenomination}
                      disabled={!newTakaQuantity || !newTakaValue}
                      className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed">
                      Add Taka
                    </button>
                  </div>
                </div>
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseTakaModal}
                sx={{
                  color: "rgba(0, 0, 0, 0.6)",
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "8px",
                  padding: "8px 16px",
                }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Receive Design Section */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setIsDesignSectionCollapsed(prev => !prev)}>
              <div className="flex items-center gap-1.5">
                <ChevronRight
                  size={16}
                  className={`text-gray-500 transition-transform ${
                    !isDesignSectionCollapsed ? "rotate-90" : ""
                  }`}
                />
                <div className="flex items-center gap-4">
                  <h2 className="text-base font-medium text-gray-800">
                    Receive Designs
                  </h2>
                  {!isDesignSectionCollapsed && (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 180,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#f8fafc",
                          "&:hover": {
                            backgroundColor: "#f1f5f9",
                          },
                          "& fieldset": {
                            borderColor: "#e2e8f0",
                          },
                          "&:hover fieldset": {
                            borderColor: "#94a3b8",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#5652B0",
                          },
                        },
                      }}>
                      <Select
                        value={designReceiveMode}
                        onChange={e => setDesignReceiveMode(e.target.value)}
                        displayEmpty
                        className="text-sm font-medium"
                        onClick={e => e.stopPropagation()}
                        sx={{
                          "& .MuiSelect-select": {
                            padding: "8px 14px",
                          },
                        }}>
                        <MenuItem
                          value="overall"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Overall Receive
                        </MenuItem>
                        <MenuItem
                          value="colour_wise"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Colour Wise Receive
                        </MenuItem>
                        <MenuItem
                          value="size_wise"
                          sx={{
                            borderRadius: "8px",
                            margin: "4px",
                            "&:hover": {
                              backgroundColor: "#f1f5f9",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "#5652B0 !important",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#4A47A3 !important",
                              },
                            },
                          }}>
                          Size Wise Receive
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </div>
                {isDesignSectionCollapsed && (
                  <span className="text-sm text-gray-500">
                    {receiveDesigns.length > 0 ? (
                      <>
                        :{" "}
                        {receiveDesigns.map(
                          (design, index) =>
                            design.name +
                            (index < receiveDesigns.length - 1 ? ", " : ""),
                        )}
                      </>
                    ) : (
                      ": No designs added yet"
                    )}
                  </span>
                )}
              </div>
              {!isDesignSectionCollapsed && (
                <div className="flex items-center gap-4">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={receiveAllDesigns}
                        onChange={e => setReceiveAllDesigns(e.target.checked)}
                        sx={{
                          color: "#5652B0",
                          "&.Mui-checked": {
                            color: "#5652B0",
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm font-medium text-gray-700">
                        Receive All
                      </span>
                    }
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="h-6 w-px bg-gray-500 mx-2" />{" "}
                  {/* Vertical separator */}
                  <div className="flex items-center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={readyForPackaging}
                          onChange={() => handleDesignActionChange("packaging")}
                          sx={{
                            color: "#5652B0",
                            "&.Mui-checked": {
                              color: "#5652B0",
                            },
                          }}
                        />
                      }
                      label={
                        <span className="text-sm font-medium text-gray-700">
                          Ready for Packaging
                        </span>
                      }
                      onClick={e => e.stopPropagation()}
                    />
                    <span className="text-sm font-medium text-gray-500 mr-4 flex items-center h-full">
                      OR
                    </span>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={addToStockDesigns}
                          onChange={() => handleDesignActionChange("stock")}
                          sx={{
                            color: "#5652B0",
                            "&.Mui-checked": {
                              color: "#5652B0",
                            },
                          }}
                        />
                      }
                      label={
                        <span className="text-sm font-medium text-gray-700">
                          Add to Stock
                        </span>
                      }
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                </div>
              )}
            </div>

            <div
              className={`space-y-4 ${
                isDesignSectionCollapsed ? "hidden" : "mt-4"
              }`}>
              {receiveDesigns.map(design => (
                <div key={design.id} className="space-y-4">
                  {design.pieces.map((piece, pieceIndex) => (
                    <div
                      key={pieceIndex}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-start gap-4">
                          <img
                            src={design.image || "/placeholder-design.png"}
                            alt={design.name}
                            className="w-16 h-16 object-cover rounded-lg bg-gray-100"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex items-start flex-col">
                                <div className="flex items-center gap-4">
                                  <h3 className="text-lg font-medium text-gray-800">
                                    {design.name} ({piece.pieceType})
                                  </h3>
                                  <span className="text-sm text-gray-600">
                                    {design.usedQty || 0}/
                                    {design.totalQty || piece.totalQty || 0}{" "}
                                    pieces
                                  </span>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Receive Quantity"
                                    className="min-w-[150px]"
                                    disabled={designReceiveMode !== "overall"}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          pieces
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor:
                                          designReceiveMode !== "overall"
                                            ? "#f3f4f6"
                                            : "transparent",
                                        "& fieldset": {
                                          borderColor:
                                            designReceiveMode !== "overall"
                                              ? "#d1d5db"
                                              : "#e5e7eb",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            designReceiveMode !== "overall"
                                              ? "#d1d5db"
                                              : "#9ca3af",
                                        },
                                        "& input": {
                                          color:
                                            designReceiveMode !== "overall"
                                              ? "#9ca3af"
                                              : "inherit",
                                        },
                                      },
                                      "& .MuiInputLabel-root": {
                                        color:
                                          designReceiveMode !== "overall"
                                            ? "#9ca3af"
                                            : "#6b7280",
                                        fontWeight: 300,
                                      },
                                    }}
                                  />
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  Design ID: {design.designId}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="space-y-4">
                          {(designReceiveMode === "colour_wise" ||
                            designReceiveMode === "size_wise") &&
                            piece.colors.map((color, colorIndex) => (
                              <div key={colorIndex}>
                                <div className="w-full mb-2 flex items-center gap-4 ml-3">
                                  <span className="text-sm text-left font-medium text-gray-700">
                                    Color : {color.name}
                                  </span>
                                  <span className="text-sm text-gray-600">
                                    {color.usedQty || 0}/{color.totalQty || 0}{" "}
                                    pieces
                                  </span>
                                  <TextField
                                    size="small"
                                    type="number"
                                    label="Receive Quantity"
                                    className="min-w-[150px]"
                                    disabled={designReceiveMode === "size_wise"}
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          pieces
                                        </InputAdornment>
                                      ),
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "8px",
                                        backgroundColor:
                                          designReceiveMode === "size_wise"
                                            ? "#f3f4f6"
                                            : "transparent",
                                        "& fieldset": {
                                          borderColor:
                                            designReceiveMode === "size_wise"
                                              ? "#d1d5db"
                                              : "#e5e7eb",
                                        },
                                        "&:hover fieldset": {
                                          borderColor:
                                            designReceiveMode === "size_wise"
                                              ? "#d1d5db"
                                              : "#9ca3af",
                                        },
                                        "& input": {
                                          color:
                                            designReceiveMode === "size_wise"
                                              ? "#9ca3af"
                                              : "inherit",
                                        },
                                      },
                                      "& .MuiInputLabel-root": {
                                        color:
                                          designReceiveMode === "size_wise"
                                            ? "#9ca3af"
                                            : "#6b7280",
                                        fontWeight: 300,
                                      },
                                    }}
                                  />
                                </div>
                                <div className="space-y-3">
                                  {designReceiveMode === "size_wise" &&
                                    Object.entries(color.sizes).map(
                                      ([size, total]) => {
                                        const used = 0; // This would come from actual data
                                        const receiveKey = `${design.id}-${piece.pieceType}-${color.name}-${size}`;
                                        return (
                                          <div
                                            key={size}
                                            className="flex items-center gap-4">
                                            <div className="min-w-[120px]">
                                              <span className="text-sm font-medium text-gray-700">
                                                {size}
                                              </span>
                                            </div>
                                            <div className="min-w-[200px]">
                                              <span className="text-sm text-gray-600">
                                                {used}/{total} pieces
                                              </span>
                                            </div>
                                            <div className="min-w-[150px]">
                                              <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                label="Receive Quantity"
                                                value={
                                                  receiveDesignQuantities[
                                                    receiveKey
                                                  ] || ""
                                                }
                                                onChange={e => {
                                                  const value = Math.min(
                                                    Math.max(
                                                      0,
                                                      parseInt(
                                                        e.target.value,
                                                      ) || 0,
                                                    ),
                                                    total - used,
                                                  );
                                                  setReceiveDesignQuantities(
                                                    prev => ({
                                                      ...prev,
                                                      [receiveKey]: value,
                                                    }),
                                                  );
                                                }}
                                                InputProps={{
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      pieces
                                                    </InputAdornment>
                                                  ),
                                                }}
                                                sx={{
                                                  "& .MuiOutlinedInput-root": {
                                                    borderRadius: "8px",
                                                    "& fieldset": {
                                                      borderColor: "#e5e7eb",
                                                    },
                                                    "&:hover fieldset": {
                                                      borderColor: "#d1d5db",
                                                    },
                                                  },
                                                  "& .MuiInputLabel-root": {
                                                    color: "#9ca3af",
                                                    fontWeight: 300,
                                                  },
                                                }}
                                              />
                                            </div>
                                          </div>
                                        );
                                      },
                                    )}
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Receive Other Designs Button */}
              <button
                className="w-full mt-4 px-4 py-3 text-gray-700 font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between"
                onClick={() => setShowOtherDesignsModal(true)}>
                <span>Receive Other Designs</span>
                <ChevronRight size={18} className="text-gray-500" />
              </button>

              {/* Other Designs Display Section */}
              {otherDesigns.length > 0 && (
                <div className="mt-4 space-y-4">
                  {otherDesigns.map(design => (
                    <div
                      key={design.id}
                      className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                            {design.image ? (
                              <img
                                src={design.image}
                                alt={design.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <ImageIcon size={24} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="flex items-start flex-col">
                                <div className="flex items-center gap-4">
                                  <h3 className="text-base font-medium text-gray-800">
                                    {design.name}
                                  </h3>
                                  <p className="text-sm text-gray-500">
                                    Design ID: {design.designId}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <FormControl
                                  size="small"
                                  sx={{ minWidth: 120 }}>
                                  <InputLabel>Select Piece</InputLabel>
                                  <Select
                                    value={design.pieces[0].pieceType}
                                    label="Select Piece"
                                    onChange={e => {
                                      setOtherDesigns(prev =>
                                        prev.map(d => {
                                          if (d.id === design.id) {
                                            const updatedPieces = [...d.pieces];
                                            updatedPieces[0] = {
                                              ...updatedPieces[0],
                                              pieceType: e.target.value,
                                            };
                                            return {
                                              ...d,
                                              pieces: updatedPieces,
                                            };
                                          }
                                          return d;
                                        }),
                                      );
                                    }}>
                                    <MenuItem value="Bottom">Bottom</MenuItem>
                                    <MenuItem value="Top">Top</MenuItem>
                                    <MenuItem value="Dupatta">Dupatta</MenuItem>
                                    <MenuItem value="Full">Full</MenuItem>
                                  </Select>
                                </FormControl>

                                <button
                                  onClick={() => handleAddNote(design.id)}
                                  className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors">
                                  <FileText size={18} />
                                </button>

                                <label className="p-2 text-gray-500 hover:text-[#5652B0] hover:bg-[#5652B0]/5 rounded-lg transition-colors cursor-pointer">
                                  <ImageIcon size={18} />
                                  <input
                                    type="file"
                                    className="hidden"
                                    multiple
                                    accept="image/*"
                                    onChange={e =>
                                      handleImageUpload(design.id, e)
                                    }
                                  />
                                </label>

                                <button
                                  onClick={() =>
                                    handleRemoveOtherDesign(design.id)
                                  }
                                  className="p-2 text-[#9B1C1C] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Note Display Section */}
                      {itemNotes[design.id] && (
                        <div className="px-4 py-3 border-t border-gray-100">
                          <div className="flex items-start gap-2">
                            <FileText
                              size={16}
                              className="text-gray-500 mt-0.5"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-700 mb-1">
                                Note:
                              </div>
                              <div className="text-sm text-gray-600">
                                {itemNotes[design.id]}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Image Preview Section */}
                      {itemImages[design.id] &&
                        itemImages[design.id].length > 0 && (
                          <div className="px-4 py-3 border-t border-gray-100">
                            <div className="flex flex-wrap gap-3">
                              {itemImages[design.id].map((image, index) => (
                                <div key={index} className="relative group">
                                  <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden">
                                    <img
                                      src={image}
                                      alt={`Preview ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <button
                                    onClick={() =>
                                      handleRemoveImage(design.id, index)
                                    }
                                    className="absolute -top-1.5 -right-1.5 bg-white shadow-sm border border-gray-200 text-[#9B1C1C] rounded-full p-1 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100">
                                    <X size={12} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                      <div className="p-4">
                        <div className="flex flex-col gap-2">
                          {/* Color rows section */}
                          {(design.pieces[0].colors || []).map(
                            (color, colorIndex) => (
                              <div
                                key={colorIndex}
                                className="flex items-center gap-4">
                                <div className="min-w-[200px]">
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Color</InputLabel>
                                    <Select
                                      value={color.name}
                                      label="Color"
                                      onChange={e =>
                                        handleOtherDesignColorChange(
                                          design.id,
                                          0,
                                          colorIndex,
                                          "name",
                                          e.target.value,
                                        )
                                      }>
                                      {colors.map(color => (
                                        <MenuItem key={color} value={color}>
                                          {color}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </div>

                                <div className="min-w-[120px]">
                                  <TextField
                                    fullWidth
                                    type="number"
                                    size="small"
                                    label="Quantity"
                                    value={color.quantity}
                                    onChange={e =>
                                      handleOtherDesignColorChange(
                                        design.id,
                                        0,
                                        colorIndex,
                                        "quantity",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>

                                <div className="min-w-[120px]">
                                  <FormControl size="small" fullWidth>
                                    <InputLabel>Unit</InputLabel>
                                    <Select
                                      value={color.unit}
                                      label="Unit"
                                      onChange={e =>
                                        handleOtherDesignColorChange(
                                          design.id,
                                          0,
                                          colorIndex,
                                          "unit",
                                          e.target.value,
                                        )
                                      }>
                                      <MenuItem value="meters">Meters</MenuItem>
                                      <MenuItem value="yards">Yards</MenuItem>
                                      <MenuItem value="pieces">Pieces</MenuItem>
                                    </Select>
                                  </FormControl>
                                </div>

                                <div className="min-w-[250px]">
                                  {Object.values(color.sizes || {}).some(
                                    v => v && v !== "0",
                                  ) ? (
                                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-[#5652B0] rounded-lg">
                                      <div className="flex-1 flex items-center gap-1.5">
                                        {Object.entries(color.sizes).map(
                                          ([size, qty], idx, arr) => (
                                            <React.Fragment key={size}>
                                              <span className="text-sm text-gray-700">
                                                {size}:{" "}
                                                <span className="font-medium">
                                                  {qty}
                                                </span>
                                              </span>
                                              {idx < arr.length - 1 && (
                                                <span className="text-gray-400">
                                                  •
                                                </span>
                                              )}
                                            </React.Fragment>
                                          ),
                                        )}
                                      </div>
                                      <button
                                        onClick={() =>
                                          handleAddSizeDenomination(
                                            design.id,
                                            colorIndex,
                                          )
                                        }
                                        className="p-1 text-gray-500 hover:text-[#5652B0] rounded-full hover:bg-white transition-colors">
                                        <Edit2 size={14} />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleAddSizeDenomination(
                                          design.id,
                                          colorIndex,
                                        )
                                      }
                                      className="w-full px-3 py-2 text-xs font-medium text-[#5652B0] border border-[#5652B0] rounded-lg hover:bg-[#5652B0] hover:text-white transition-all">
                                      Add Sizes Denomination
                                    </button>
                                  )}
                                </div>

                                <IconButton
                                  onClick={() =>
                                    handleRemoveOtherDesignColor(
                                      design.id,
                                      0,
                                      colorIndex,
                                    )
                                  }
                                  size="small">
                                  <Trash2
                                    size={18}
                                    className="text-[#9B1C1C] hover:text-red-600"
                                  />
                                </IconButton>
                              </div>
                            ),
                          )}

                          <div className="flex">
                            <button
                              onClick={() =>
                                handleAddOtherDesignColorRow(design.id, 0)
                              }
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-[#5652B0] hover:bg-[#4A47A3] transition-all">
                              <Plus size={16} />
                              Add Color
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Payment Due Checkbox */}
          <FormControlLabel
            className="mt-0 ml-1"
            control={
              <Checkbox
                checked={paymentDue}
                onChange={e => setPaymentDue(e.target.checked)}
                sx={{
                  color: "#5652B0",
                  "&.Mui-checked": {
                    color: "#5652B0",
                  },
                }}
              />
            }
            label={
              <span className="text-sm font-medium text-gray-700">
                Payment Due
              </span>
            }
          />

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-2">
            <button
              onClick={handleBack}
              className="px-6 py-2.5 text-gray-600 font-medium border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              // onClick={handleReceive} // You'll need to implement this
              className="px-6 py-2.5 text-white font-medium bg-[#5652B0] rounded-lg hover:bg-[#4A47A3] transition-colors">
              Receive
            </button>
          </div>
        </div>
      </div>

      {/* Size Denomination Modal */}
      <Dialog
        open={showSizeDenominationModal}
        onClose={() => setShowSizeDenominationModal(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-medium">Size Quantities Recieved</span>
          <IconButton
            onClick={() => setShowSizeDenominationModal(false)}
            size="small"
            className="text-gray-500">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <div className="space-y-3">
              {["XS", "S", "M", "L", "XL", "XXL"].map(size => (
                <div key={size} className="flex items-center gap-4">
                  <div className="w-12 text-sm font-medium text-gray-700">
                    {size}
                  </div>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="0"
                    value={
                      currentOtherDesignColor
                        ? otherDesigns.find(
                            d => d.id === currentOtherDesignColor.designId,
                          )?.pieces[0].colors[
                            currentOtherDesignColor.colorIndex
                          ]?.sizes?.[size] || ""
                        : ""
                    }
                    onChange={e =>
                      handleSizeDenominationChange(size, e.target.value)
                    }
                    InputProps={{
                      inputProps: {
                        min: 0,
                        style: { textAlign: "right" },
                      },
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSizeDenominationModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => setShowSizeDenominationModal(false)}
            variant="contained"
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Save Quantities
          </Button>
        </DialogActions>
      </Dialog>

      {/* Other Designs Selection Modal */}
      <Dialog
        open={showOtherDesignsModal}
        onClose={() => setShowOtherDesignsModal(false)}
        maxWidth="md"
        fullWidth>
        <DialogTitle className="flex justify-between items-center">
          <span className="text-lg font-medium">Select Other Designs</span>
          <IconButton
            onClick={() => setShowOtherDesignsModal(false)}
            size="small"
            className="text-gray-500">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="py-4">
            <TextField
              fullWidth
              size="small"
              placeholder="Search designs..."
              value={otherDesignSearchQuery}
              onChange={e => setOtherDesignSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} className="text-gray-500" />
                  </InputAdornment>
                ),
              }}
            />

            <div className="mt-4 space-y-2 max-h-[400px] overflow-y-auto">
              {availableOtherDesigns
                .filter(design =>
                  design.name
                    .toLowerCase()
                    .includes(otherDesignSearchQuery.toLowerCase()),
                )
                .map(design => (
                  <div
                    key={design.id}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                      tempSelectedOtherDesigns.some(d => d.id === design.id)
                        ? "border-[#5652B0] bg-purple-50"
                        : "border-gray-200 hover:border-[#5652B0]"
                    }`}
                    onClick={() => handleOtherDesignSelect(design)}>
                    <Checkbox
                      checked={tempSelectedOtherDesigns.some(
                        d => d.id === design.id,
                      )}
                      sx={{
                        color: "#5652B0",
                        "&.Mui-checked": {
                          color: "#5652B0",
                        },
                      }}
                    />
                    <div className="w-16 h-16 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex-shrink-0 mr-3">
                      {design.image ? (
                        <img
                          src={design.image}
                          alt={design.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <ImageIcon size={24} />
                        </div>
                      )}
                    </div>
                    <div className="ml-2 flex-grow">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-base">{design.name}</p>
                          <p className="text-sm text-gray-500">
                            Design ID: {design.designId}
                          </p>
                        </div>
                        <p className="text-sm font-medium">
                          ₹{design.price || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowOtherDesignsModal(false)}
            sx={{
              color: "gray",
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedOtherDesigns}
            variant="contained"
            disabled={tempSelectedOtherDesigns.length === 0}
            sx={{
              backgroundColor: "#5652B0",
              "&:hover": {
                backgroundColor: "#4A47A3",
              },
            }}>
            Add Selected ({tempSelectedOtherDesigns.length})
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReceiveJobslip;
